import {
  AccountType,
  BenificiaryType,
  Currency,
  ValidatorsProps,
} from '@ui/src';

export interface UploadResponse {
  userId: string;
  mimeType: string;
  size: number;
  id: string;
  createdAt: string;
  name: string;
}

export enum CurrencyStatus {
  ACTIVE = 'Active',
  DISABLED = 'Disabled',
}

export enum CountryStatus {
  ACTIVE = 'Active',
  DISABLED = 'Disabled',
}

export interface CurrencyItem {
  code: number;
  decimals: number;
  description: string;
  name: string;
  id: string;
  active: boolean;
  url?: string;
  shouldCreateBalanceForClients?: boolean;
  render?: Record<string, React.JSX.Element>;
}

export interface CountryItem {
  id: string;
  name: string;
  alpha3: number;
  alpha2: number;
  numeric: string;
  active: boolean;
  riskScore?: string | number;
  render?: Record<string, React.JSX.Element>;
}

export enum TransactionTypeAllowedTransfersEnum {
  ANY_TRANSFERS = 'ANY_TRANSFERS',
  OWN_ACCOUNTS = 'OWN_ACCOUNTS',
}
export interface TransactionType {
  id: string;
  name: string;
  active: boolean;
  allowedTransfers?: TransactionTypeAllowedTransfersEnum[];
  children?: TransactionSubtype[];
}

export interface TransactionSubtype {
  id: string;
  name: string;
  active: boolean;
  parents: string[];
}

export interface PSPValidator {
  createSettingSchema: ValidatorsProps;
  updateSettingSchema: ValidatorsProps;
}

export interface PSPDetails {
  createdAt: string;
  data: {
    value:
      | string
      | boolean
      | string[]
      | Record<string, string>
      | Record<string, boolean>;
  };
  deletedAt: string;
  id: string;
  key: string;
  updatedAt: string;
}

export interface RateSource {
  name: string;
  description: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export interface EditPSPDetailsFormValues {
  bic: string;
  sortcode: string;
  finyearstart: string;
  vatPercent?: string | number;
  countryId: string;
  address: string;
  website: string;
  email: string;
  phone: string;
  license: string;
  regulator: string;
  customersDemoModeAvailable?: boolean;
  legalName: string;
}

export interface EditPSPBrandFormValues {
  companyName: string;
  brandColor: string;
  cardLogo: string;
  favicon: string;
  headerLogo: string;
  loaderIcon: string;
}

export interface EditPSPAuthorizationFormValues {
  publicRegistrationEnabled: boolean;
  turnstileEnabled: boolean;
  disableTwilioAutoThrottle: boolean;
  disableAutoLocks: boolean;
  // turnstileForceEnabled: boolean;
}

export interface EditPSPRatesFormValues {
  baseRateSource: string;
  exchangeRateSource: string;
}

export interface EditPSPWebhooksFormValues {
  applicationChange: boolean;
  organizationChange: boolean;
  organizationCreate: boolean;
  transactionChange: boolean;
  webHookUri: string;
}

export interface EditPSPLanguagesFormValues {
  applicationsAvailableLanguages: string[];
  applicationsNotAvailableLanguages: string[];
  documentsAvailableLanguages: string[];
  documentsNotAvailableLanguages: string[];
  transactionsAvailableLanguages: string[];
  transactionsNotAvailableLanguages: string[];
}

export interface EditPSPOTPFormValues {
  otpAuthAvailableMethods?: string[];
  otpAuthDefaultMethod?: string;
  otpTransactionSignAvailableMethods?: string[];
  otpTransactionSignDefaultMethod?: string;
  otpApplicationSignAvailableMethods?: string[];
  otpApplicationSignDefaultMethod?: string;
  otpAgreementSignAvailableMethods?: string[];
  otpAgreementSignDefaultMethod?: string;
}

export enum PSPOTPType {
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
  TOTP = 'TOTP',
}

export enum PSPDetailsKeys {
  BIC = 'BIC',
  SORTCODE = 'SORTCODE',
  FIN_YEAR_START = 'FIN_YEAR_START',
  VAT_PERCENT = 'VAT-PERCENT',
  COUNTRY = 'COUNTRY',
  ADDRESS = 'ADDRESS',
  WEBSITE = 'WEBSITE',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  LICENSE = 'LICENSE',
  REGULATOR = 'REGULATOR',
  CUSTOMERS_DEMO_MODE_AVAILABLE = 'CUSTOMERS-DEMO-MODE-AVAILABLE',
  LEGAL_NAME = 'LEGAL_NAME',
  PAYMENTS_ORDERS_ENABLED = 'PAYMENTS-ORDERS-ENABLED',
  CARDS = 'CARDS',
  TOP_UP = 'TRANSACTIONS-TOP-UP-BUTTON-NAME',
  STATEMENTS = 'STATEMENTS-ACCOUNT-DETAILS-SOURCE',
}

export enum PSPLanguangesKeys {
  APPLICATIONS_AVAILABLE_LANGUAGES = 'APPLICATIONS-AVAILABLE-LANGUAGES',
  APPLICATIONS_NOT_AVAILABLE_LANGUAGES = 'APPLICATIONS-NOT-AVAILABLE-LANGUAGES',
  DOCUMENTS_AVAILABLE_LANGUAGES = 'DOCUMENTS-AVAILABLE-LANGUAGES',
  DOCUMENTS_NOT_AVAILABLE_LANGUAGES = 'DOCUMENTS-NOT-AVAILABLE-LANGUAGES',
  TRANSACTIONS_AVAILABLE_LANGUAGES = 'TRANSACTIONS-AVAILABLE-LANGUAGES',
  TRANSACTIONS_NOT_AVAILABLE_LANGUAGES = 'TRANSACTIONS-NOT-AVAILABLE-LANGUAGES',
}

export enum PSPBrandKeys {
  WHITELABEL = 'WHITELABEL',
}

export enum PSPAuthorizationKeys {
  PUBLIC_REGISTRATION_ENABLED = 'PUBLIC-REGISTRATION-ENABLED',
  TURNSTILE_ENABLED = 'TURNSTILE-ENABLED',
  DISABLE_TWILIO_AUTO_THROTTLE = 'DISABLE-TWILIO-AUTO-THROTTLE',
  DISABLE_AUTO_LOCKS = 'DISABLE-AUTO-LOCKS',
  // TURNSTILE_FORCE_ENABLED = 'TURNSTILE-FORCE-ENABLED',
}

export enum PSPOTPKeys {
  OTP_CUSTOMERS_AUTH_DEFAULT_METHOD = 'OTP-CUSTOMERS-AUTH-DEFAULT-METHOD',
  OTP_CUSTOMERS_AUTH_AVAILABLE_METHODS = 'OTP-CUSTOMERS-AUTH-AVAILABLE-METHODS',
  OTP_CUSTOMERS_TRANSACTION_SIGN_DEFAULT_METHOD = 'OTP-CUSTOMERS-TRANSACTION-SIGN-DEFAULT-METHOD',
  OTP_CUSTOMERS_TRANSACTION_SIGN_AVAILABLE_METHODS = 'OTP-CUSTOMERS-TRANSACTION-SIGN-AVAILABLE-METHODS',
  OTP_CUSTOMERS_APPLICATION_SIGN_AVAILABLE_METHODS = 'OTP-CUSTOMERS-APPLICATION-SIGN-AVAILABLE-METHODS',
  OTP_CUSTOMERS_APPLICATION_SIGN_DEFAULT_METHOD = 'OTP-CUSTOMERS-APPLICATION-SIGN-DEFAULT-METHOD',
  OTP_CUSTOMERS_AGREEMENT_SIGN_AVAILABLE_METHODS = 'OTP-CUSTOMERS-AGREEMENT-SIGN-AVAILABLE-METHODS',
  OTP_CUSTOMERS_AGREEMENT_SIGN_DEFAULT_METHOD = 'OTP-CUSTOMERS-AGREEMENT-SIGN-DEFAULT-METHOD',
}

export enum PSPRatesKeys {
  BASE_RATE_SOURCE = 'BASE-RATE-SOURCE',
  EXCHANGE_RATE_SOURCE = 'EXCHANGE-RATE-SOURCE',
}

export enum PSPWebhooksKeys {
  WEBHOOK_EVENTS = 'WEBHOOK_EVENTS',
  WEBHOOK_URI = 'WEBHOOK_URI',
}

export enum TransactionsSettingsKeys {
  CORPORATE_BENEFICIARIES_CONTRACTS_ENABLED = 'CORPORATE-BENEFICIARIES-CONTRACTS-ENABLED',
  CORPORATE_BENEFICIARIES_MODERATION_ENABLED = 'CORPORATE-BENEFICIARIES-MODERATION-ENABLED',
  CORPORATE_BENEFICIARIES_CONTRACTS_MODERATION_ENABLED = 'CORPORATE-BENEFICIARIES-CONTRACTS-MODERATION-ENABLED',
  CORPORATE_BENEFICIARIES_CONTRACTS_REQUIRED_ENABLED = 'CORPORATE-BENEFICIARIES-CONTRACTS-REQUIRED-ENABLED',
  CORPORATE_BENEFICIARIES_ATTACH_CONTRACTS_ENABLED = 'CORPORATE-BENEFICIARIES-ATTACH-CONTRACTS-ENABLED',
  CORPORATE_TRANSACTION_ATTACH_DOCUMENTS_ENABLED = 'CORPORATE-TRANSACTION-ATTACH-DOCUMENTS-ENABLED',
  CORPORATE_TRANSACTION_ATTACH_DOCUMENTS_REQUIRED = 'CORPORATE-TRANSACTION-ATTACH-DOCUMENTS-REQUIRED',
  INDIVIDUAL_TRANSACTION_ATTACH_DOCUMENTS_ENABLED = 'INDIVIDUAL-TRANSACTION-ATTACH-DOCUMENTS-ENABLED',
  INDIVIDUAL_TRANSACTION_ATTACH_DOCUMENTS_REQUIRED = 'INDIVIDUAL-TRANSACTION-ATTACH-DOCUMENTS-REQUIRED',
  CORPORATE_CUSTOMERS_API_OUTGOING_PSP_PARTNER_ENABLED = 'CORPORATE-CUSTOMERS-API-OUTGOING-PSP-PARTNER-ENABLED',
  CORPORATE_CUSTOMERS_API_OUTGOING_PSP_PARTNER_REQUIRED = 'CORPORATE-CUSTOMERS-API-OUTGOING-PSP-PARTNER-REQUIRED',
  INDIVIDUAL_CUSTOMERS_API_OUTGOING_PSP_PARTNER_ENABLED = 'INDIVIDUAL-CUSTOMERS-API-OUTGOING-PSP-PARTNER-ENABLED',
  INDIVIDUAL_CUSTOMERS_API_OUTGOING_PSP_PARTNER_REQUIRED = 'INDIVIDUAL-CUSTOMERS-API-OUTGOING-PSP-PARTNER-REQUIRED',
  AML_BOT_ENABLED = 'AML-BOT-ENABLED',
  AML_BOT_TRANSACTIONS_TYPES = 'AML-BOT-TRANSACTIONS-TYPES',
  AML_BOT_CURRENCIES = 'AML-BOT-CURRENCIES',
  OVERDRAFT_ENABLED = 'OVERDRAFT-ENABLED',
  OVERDRAFT_VISIBLE_TO_CLIENTS = 'OVERDRAFT-VISIBLE-TO-CLIENTS',
  EXCHANGE_CUSTOM_RATE_ENABLED = 'EXCHANGE-CUSTOM-RATE-ENABLED',
  ADDRESS_MAX_LENGTH = 'ADDRESS-MAX-LENGTH',
  PURPOSES_OF_PAYMENT_MAX_LENGTH = 'PURPOSES-OF-PAYMENT-MAX-LENGTH',
  SWIFT_MT_CHAR_SET_VALIDATION = 'SWIFT-MT-CHAR-SET-VALIDATION',
  SWIFT_ISO20022_CHAR_SET_VALIDATION = 'SWIFT-ISO20022-CHAR-SET-VALIDATION',
  IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_CUSTOMERS = 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-CUSTOMERS',
  IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_CUSTOMERS = 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-CUSTOMERS',
  ENABLE_BIC_DATA_FROM_EXTERNAL_SOURCE_EDIT = 'ENABLE-BIC-DATA-FROM-EXTERNAL-SOURCE-EDIT',
  ENABLE_IBAN_DATA_FROM_EXTERNAL_SOURCE_EDIT = 'ENABLE-IBAN-DATA-FROM-EXTERNAL-SOURCE-EDIT',
  IBAN_COM_IBAN_VALIDATION_AVAILABLE_FOR_EMPLOYEES = 'IBAN-COM-IBAN-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
  IBAN_COM_BIC_VALIDATION_AVAILABLE_FOR_EMPLOYEES = 'IBAN-COM-BIC-VALIDATION-AVAILABLE-FOR-EMPLOYEES',
  AML_BOT_ADDRESS_AUTORESOLVE_ENABLE = 'AML-BOT-ADDRESS-AUTORESOLVE-ENABLE',
  AML_BOT_ADDRESS_AUTORESOLVE_VALUE = 'AML-BOT-ADDRESS-AUTORESOLVE-VALUE',
  AML_BOT_ADDRESS_AUTODECLINE_ENABLE = 'AML-BOT-ADDRESS-AUTODECLINE-ENABLE',
  AML_BOT_ADDRESS_AUTODECLINE_VALUE = 'AML-BOT-ADDRESS-AUTODECLINE-VALUE',
  AML_BOT_INCOMING_TX_AUTORESOLVE_ENABLE = 'AML-BOT-INCOMING-TX-AUTORESOLVE-ENABLE',
  AML_BOT_INCOMING_TX_AUTORESOLVE_VALUE = 'AML-BOT-INCOMING-TX-AUTORESOLVE-VALUE',
  AML_BOT_INCOMING_TX_AUTODECLINE_ENABLE = 'AML-BOT-INCOMING-TX-AUTODECLINE-ENABLE',
  AML_BOT_INCOMING_TX_AUTODECLINE_VALUE = 'AML-BOT-INCOMING-TX-AUTODECLINE-VALUE',
  CRYPTOSWIFT_ENABLED = 'CRYPTOSWIFT-ENABLED',
}

export enum ApplicationSchemeSettingsKeys {
  APPLICATION_SCHEME_COMPANY_OFFICIAL = 'APPLICATION-SCHEME-COMPANY-OFFICIAL',
  APPLICATION_SCHEME_INDIVIDUAL = 'APPLICATION-SCHEME-INDIVIDUAL',
  APPLICATION_SCHEME_CORPORATE = 'APPLICATION-SCHEME-CORPORATE',
  APPLICATION_SCHEME_COMPANY_OFFICIAL_UBO = 'APPLICATION-SCHEME-COMPANY-OFFICIAL-UBO',
  MINIMUM_UBO_OWNERSHIP_PERCENTAGE = 'MINIMUM-UBO-OWNERSHIP-PERCENTAGE',
}

export interface IbanValidationFormValues {
  ibanComIbanValidationAvailableForCustomers?: boolean;
  ibanComBicValidationAvailableForCustomers?: boolean;
  enableBicDataFromExternalSourceEdit?: boolean;
  enableIbanDataFromExternalSourceEdit?: boolean;
  ibanComIbanValidationAvailableForEmployees?: boolean;
  ibanComBicValidationAvailableForEmployees?: boolean;
}

export interface BeneficiariesSettingsVormValues {
  beneficiariesModerationEnabled?: boolean;
  beneficiariesContractsEnabled?: boolean;
  beneficiariesContractsModerationEnabled?: boolean;
  beneficiariesAttachContractsEnabled?: boolean;
  beneficiariesContractsRequiredEnabled?: boolean;
  transactionAttachDocumentsEnabled?: boolean;
  transactionAttachDocumentsRequired?: boolean;
  individualTransactionAttachDocumentsEnabled?: boolean;
  individualTransactionAttachDocumentsRequired?: boolean;
  corporateTransactionAttachDocumentsEnabled?: boolean;
  corporateTransactionAttachDocumentsRequired?: boolean;
  individualTransactionOutgoingPspPartnerEnabled?: boolean;
  individualTransactionOutgoingPspPartnerRequired?: boolean;
  corporateTransactionOutgoingPspPartnerEnabled?: boolean;
  corporateTransactionOutgoingPspPartnerRequired?: boolean;
  amlBotEnabled?: boolean;
  amlBotTransactionsTypes?: string[];
  amlBotCurrencies?: string[];
  exchangeCustomRateEnabled?: boolean;
  addressMaxLength?: string | number;
  purposeOfPaymentMaxLength?: string | number;
  swiftMTCharSetValidation?: boolean;
  swiftISO20022CharSetValidation?: boolean;
  amlBotAddressAutoresolveEnable?: boolean;
  amlBotAddressAutoresolveValue?: string | number | null;
  amlBotAddressAutodeclineEnable?: boolean;
  amlBotAddressAutodeclineValue?: string | number | null;
  amlBotIncomingTxAutoresolveEnable?: boolean;
  amlBotIncomingTxAutoresolveValue?: string | number | null;
  amlBotIncomingTxAutodeclineEnable?: boolean;
  amlBotIncomingTxAutodeclineValue?: string | number | null;
  cryptoSwiftEnabled?: boolean;
}

export enum SettingClientRangeType {
  MONTHLY_TURNOVER = 'MONTHLY_TURNOVER',
  MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS = 'MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS',
  MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS = 'MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS',
  MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS = 'MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS',
  MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS = 'MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS',
  MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT = 'MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT',
  MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT = 'MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT',
}

export interface SettingClientRange {
  type: SettingClientRangeType;
  from: number;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  riskScore?: number;
  clientType?: BenificiaryType;
  render?: Record<string, React.JSX.Element>;
}

export interface CreateSettingsRangeForm {
  from: number;
  clientType: BenificiaryType;
}

export interface CreateSettingsRangeRequest extends CreateSettingsRangeForm {
  type: SettingClientRangeType;
}

export interface SettingRangeValidator {
  createSchema: ValidatorsProps;
}

export enum CustomerType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
}

export enum AgreementStatus {
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum AgreementType {
  GENERAL_T_AND_CS = 'GENERAL_T_AND_CS',
  PRIVACY_NOTICE = 'PRIVACY_NOTICE',
  ANNEX_1 = 'ANNEX_1',
  ANNEX_2 = 'ANNEX_2',
  ANNEX_3 = 'ANNEX_3',
  ANNEX_4 = 'ANNEX_4',
  ANNEX_5 = 'ANNEX_5',
  ANNEX_6 = 'ANNEX_6',
  ANNEX_7 = 'ANNEX_7',
  ANNEX_8 = 'ANNEX_8',
}

export enum AgreementClientType {
  INDIVIDUAL = 'INDIVIDUAL',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
  CORPORATE = 'CORPORATE',
}

export interface AgreementFormValues {
  type?: string;
  client?: string;
  uploadId?: string;
  description?: string;
  version?: string;
  startAt?: string;
}

export interface AgreementBody {
  type?: string;
  client?: string;
  uploadId?: string;
  description?: string;
  version?: number;
  startAt?: string;
}

export interface Agreement {
  type: AgreementType;
  client: AgreementClientType;
  uploadId: string;
  url?: string;
  version: number;
  description: string;
  startAt: string;
  archivedAt: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  status?: AgreementStatus;
  render?: Record<string, React.JSX.Element>;
}

export interface PaymentType {
  createdAt: string;
  enabled: boolean;
  id: string;
  name: string;
  paymentType: string;
  visibleToClients: boolean;
  currencies: {
    edges: {
      node: Currency;
    }[];
  };
}

export interface CASPCurrency {
  active: boolean;
  caspId: string;
  createdAt: string;
  currencyId: string;
  currency: Currency;
  id: string;
}

export interface CASP {
  createdAt: string;
  id: string;
  name: string;
  active: boolean;
  currencies: CASPCurrency[];
}

export interface PspPartner {
  id: string;
  companyName: string;
  description?: string;
}

export interface PspAccount {
  id: string;
  description: string;
  pspPartnerId: string;
  pspPartner: PspPartner;
  account?: {
    clientName: string;
    id: string;
    fullNumber: string;
    description: string;
    overdraftEnabled: boolean;
  };
}

export interface Account {
  active: boolean;
  closed: boolean;
  debitBlock: boolean;
  creditBlock: boolean;
  description: string;
  iban: string;
  number: string;
  balance: number;
  fullNumber: string;
  groupId: string;
  primaryId: string;
  userId: string;
  organizationId: string;
  id: string;
  clientName: string;
  categoryId: string;
  accountType: AccountType;
  closedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  pspAccount?: PspAccount;
  overdraftEnabled?: boolean;
  revaluable: boolean;
}

export interface PurposeCodeSettings {
  id: string;
  purposeCodeType: string;
  paymentCategoryId: string;
  bankCountry: string;
  currenciesIds: string[];
  required?: boolean;
}

export interface PurposeCodeSettingsFormValues {
  purposeCodeType: string;
  paymentCategoryId: string;
  bankCountry: string;
  currenciesIds: string[];
  required?: boolean;
}

export interface CASPAlias {
  caspId: string;
  alias: string;
  source: string;
  id: string;
}

export interface CASPAliasFormValues {
  caspId: string;
  alias: string;
  source: string;
  id?: string;
  mode?: string;
}

export interface AMLBotAssets {
  advanced: string[];
  fast: string[];
  lite: string[];
}
